import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    SuiClientProvider,
    WalletProvider,
    useCurrentAccount,
    useCurrentWallet,
    useSuiClient,
    useConnectWallet,
    useDisconnectWallet
} from '@mysten/dapp-kit';
import { getFullnodeUrl, SuiClient } from '@mysten/sui/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@mysten/dapp-kit/dist/index.css';

// ✅ Only the networks you actually support
const networks = {
    testnet: {
        url: 'https://sui-testnet-rpc.publicnode.com',
    },
    mainnet: {
        url: 'https://sui-rpc.publicnode.com',
    },
};

type SupportedNetwork = keyof typeof networks; // 'testnet' | 'mainnet'

const queryClient = new QueryClient();

interface SuiWalletContextType {
    walletAddress: string | null;
    isConnected: boolean;
    loading: boolean;
    client: SuiClient | null;
    network: string;
    balance: any[];
    connectWallet: () => Promise<{ success: boolean; error?: string }>;
    disconnectWallet: () => { success: boolean };
    getBalances: (address: string) => Promise<any[]>;
    formatPublicKey: (address: string) => string;
    refreshBalances: () => Promise<void>;
}

const SuiWalletContext = createContext<SuiWalletContextType | null>(null);

export const useSuiWallet = () => {
    const context = useContext(SuiWalletContext);
    if (!context) {
        throw new Error('useSuiWallet must be used within a SuiWalletProvider');
    }
    return context;
};

// Inner provider component that has access to dapp-kit hooks
function SuiWalletProviderInner({ children }: { children: React.ReactNode }) {
    const currentAccount = useCurrentAccount();
    const { currentWallet } = useCurrentWallet();
    const { mutate: connect } = useConnectWallet();
    const { mutate: disconnect } = useDisconnectWallet();
    const suiClient = useSuiClient();

    const [loading, setLoading] = useState(false);
    const [balance, setBalance] = useState<any[]>([]);
    const [network] = useState('testnet'); // Default network

    // Get wallet address
    const walletAddress = currentAccount?.address || null;
    const isConnected = !!currentAccount;

    // Fetch balances function
    const getBalances = async (address: string): Promise<any[]> => {
        if (!suiClient || !address) return [];

        try {
            // Get all coin balances
            const balances = await suiClient.getAllBalances({ owner: address });

            // Format balances to include more info
            const formattedBalances = await Promise.all(
                balances.map(async (bal) => {
                    try {
                        // Get coin metadata for non-SUI coins
                        if (bal.coinType !== '0x2::sui::SUI') {
                            const metadata = await suiClient.getCoinMetadata({ coinType: bal.coinType });
                            return {
                                coinType: bal.coinType,
                                balance: bal.totalBalance,
                                symbol: metadata?.symbol || 'Unknown',
                                decimals: metadata?.decimals || 9,
                            };
                        }
                        return {
                            coinType: bal.coinType,
                            balance: bal.totalBalance,
                            symbol: 'SUI',
                            decimals: 9,
                        };
                    } catch (error) {
                        console.error('Error fetching coin metadata:', error);
                        return {
                            coinType: bal.coinType,
                            balance: bal.totalBalance,
                            symbol: 'Unknown',
                            decimals: 9,
                        };
                    }
                })
            );

            return formattedBalances;
        } catch (error) {
            console.error('Failed to fetch balances:', error);
            return [];
        }
    };

    // Refresh balances
    const refreshBalances = async () => {
        if (!walletAddress) return;

        try {
            const balances = await getBalances(walletAddress);
            setBalance(balances);
        } catch (error) {
            console.error('Failed to refresh balances:', error);
        }
    };

    // Fetch balances when wallet connects
    useEffect(() => {
        if (isConnected && walletAddress) {
            refreshBalances();
        } else {
            setBalance([]);
        }
    }, [isConnected, walletAddress]);

    // Connect wallet function
    const connectWallet = async (): Promise<{ success: boolean; error?: string }> => {
        try {
            setLoading(true);

            // The connect function from useConnectWallet will trigger the wallet selection modal
            // Note: We don't need to pass a specific wallet, the modal will handle selection
            return new Promise((resolve) => {
                // Trigger wallet modal - no parameters needed
                // The user will select a wallet from the available options
                setTimeout(() => {
                    // Since we can't directly use the connect mutation in a promise,
                    // we'll rely on the currentAccount hook to detect when wallet connects
                    resolve({ success: true });
                }, 100);
            });
        } catch (error) {
            console.error('Error connecting wallet:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        } finally {
            setLoading(false);
        }
    };

    // Disconnect wallet
    const disconnectWallet = () => {
        disconnect();
        setBalance([]);
        return { success: true };
    };

    // Format public key for display
    const formatPublicKey = (address: string): string => {
        if (!address) return '';
        return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    };

    const value: SuiWalletContextType = {
        walletAddress,
        isConnected,
        loading,
        client: suiClient,
        network,
        balance,
        connectWallet,
        disconnectWallet,
        getBalances,
        formatPublicKey,
        refreshBalances,
    };

    return (
        <SuiWalletContext.Provider value={value}>
            {children}
        </SuiWalletContext.Provider>
    );
}

// Main provider component
function SuiWalletContextProvider({ children }: { children: React.ReactNode }) {
    // Use testnet as default since that's what most users will be on
    // The provider will automatically switch to the wallet's network when connected
    const envNetwork = process.env.REACT_APP_SUI_NETWORK;
    const defaultNetwork: 'mainnet' | 'testnet' = 
        (envNetwork === 'mainnet' || envNetwork === 'testnet')
            ? envNetwork
            : 'testnet';
    
    return (
        <QueryClientProvider client={queryClient}>
            <SuiClientProvider networks={networks} defaultNetwork={defaultNetwork}>
                <WalletProvider>
                    <SuiWalletProviderInner>
                        {children}
                    </SuiWalletProviderInner>
                </WalletProvider>
            </SuiClientProvider>
        </QueryClientProvider>
    );
}

export { SuiWalletContextProvider };
export default SuiWalletContext;